import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useT } from '@hana/i18n';
import { IconBell, IconChartBar, IconChecklist, IconLock, IconPackageImport, IconPlus, IconRefresh, IconSearch } from '@tabler/icons-react';
import { DataError, useList } from '@hana/data';
import { HeaderTool, Page, PageHeader, useLayout } from '@hana/shell';
import { ImportCompact } from './ImportCompact';
import { Button, Segmented, Skeleton, StateView, useToast, type ScreenState } from '@hana/ui';
import { freeLeft, isDoLocked, LANE_LABEL, norm, quickContainers, quickDecls, quickShipments } from './logic';
import { TODAY } from './fixtures';
import { ImportDock } from './ImportDock';
import { buildQv, ImportDetail } from './ImportDetail';
import { ClaimDrawer, ImportModal, type ModalKind, type ModalReq } from './ImportModals';
import type { Claim, ClaimState, ContainerFree, CustomsDecl, Deposit, ImportShipment, ImportTab, ReeferCont } from './types';
import s from './import.module.css';

// Mint-Import (1536): 6 tab · tìm + lọc nhanh · bảng. money = hide nên các cột tiền không dựng. Chỉ đọc; nút ghi báo toast.

type Tone = 'ok' | 'warn' | 'err' | 'pri' | 'vio' | 'mute';
type Cell =
  | { t: 'tx'; k: string; s?: string; fw?: boolean; tone?: 'link' | 'ok' | 'warn' | 'err' | 'lb' | 'tx2'; al?: 'r' }
  | { t: 'chip'; k: string; tone: Tone; s?: string }
  | { t: 'clock'; left: number; total: number; s: string }
  | { t: 'btn'; k: string; tone: 'pri' | 'warn' | 'mute'; tip?: string; modal?: ModalKind };
interface Row { id: string; text: string; cells: Cell[] }
interface Sum { k: string; v: string; tone?: 'warn' | 'err' }
interface TabDef {
  label: string;
  quick: readonly (readonly [string, string])[];
  cols: readonly (readonly [string, number])[];
  foot: string;
  emptyK: string;
  emptyS: string;
}

const TABS: Record<ImportTab, TabDef> = {
  imp: { label: 'Hàng nhập', foot: 'Hàng nhập', emptyK: 'Không có lô nhập trong kỳ', emptyS: 'Lô nhập xuất hiện khi đại lý gửi pre-alert hoặc Sale tạo job nhập.', quick: [['all', 'Tất cả'], ['lock', 'DO bị khoá'], ['eta', 'ETA 3 ngày']], cols: [['Job · khách', 230], ['Hàng', 170], ['Tàu · tuyến', 200], ['ETA', 104], ['Pre-alert', 92], ['DO', 220], ['', 140], ['Chứng từ', 108]] },
  dd: { label: 'DEM/DET & cắm điện', foot: 'DEM/DET & cắm điện', emptyK: 'Không có cont đang tính free time', emptyS: 'Cont xuất hiện khi có ngày dỡ hàng (DEM) hoặc ngày lấy cont (DET).', quick: [['all', 'Tất cả'], ['soon', 'Sắp hết ≤ 2 ngày'], ['over', 'Đã quá hạn']], cols: [['Container', 160], ['Job · khách', 210], ['Hãng · loại', 120], ['Free time hãng / khách', 150], ['Đồng hồ (theo khách)', 314], ['', 310]] },
  lane: { label: 'Luồng HQ', foot: 'Luồng hải quan', emptyK: 'Chưa có kết quả phân luồng', emptyS: 'Kết quả phân luồng lấy từ dịch vụ CCF khi tờ khai được thông quan.', quick: [['all', 'Tất cả'], ['ry', 'Vàng / đỏ / soi'], ['fee', 'Phí ngoài biểu']], cols: [['Tờ khai', 150], ['Job · khách', 220], ['Luồng', 108], ['Lịch kiểm', 230], ['Phí ngoài biểu', 230], ['', 326]] },
  rf: { label: 'Hàng lạnh', foot: 'Hàng lạnh', emptyK: 'Không có cont lạnh đang chạy', emptyS: 'Cont lạnh xuất hiện khi job có loại cont RF.', quick: [['all', 'Tất cả'], ['dev', 'Lệch nhiệt độ'], ['miss', 'Thiếu ảnh / PTI']], cols: [['Container', 150], ['Job · khách', 220], ['Cài đặt · vent', 140], ['Ảnh lúc nhận cont', 210], ['Ảnh lúc hạ cont', 190], ['PTI', 110], ['', 244]] },
  dep: { label: 'Cược container', foot: 'Cược container', emptyK: 'Không có khoản cược đang chờ hoàn', emptyS: 'Cược ghi khi đóng cược lấy cont nhập.', quick: [['all', 'Tất cả'], ['late', 'Quá hạn hoàn'], ['ded', 'Có khoản trừ']], cols: [['Container · hãng', 170], ['Khách', 180], ['Trả rỗng → dự kiến hoàn', 190], ['Khoản trừ', 200], ['Quyết định', 150], ['', 374]] },
  clm: { label: 'Sự cố & claim', foot: 'Sự cố & claim', emptyK: 'Chưa có hồ sơ sự cố', emptyS: 'Mở hồ sơ từ job, từ cảnh báo hàng lạnh hoặc từ chuyến xe.', quick: [['all', 'Tất cả'], ['open', 'Đang mở'], ['bod', 'Chờ BOD']], cols: [['Hồ sơ · job', 180], ['Khách', 170], ['Mô tả', 290], ['Bên có lỗi', 110], ['Hạn thông báo', 120], ['Trạng thái', 130]] },
};
const ORDER: readonly ImportTab[] = ['imp', 'dd', 'lane', 'rf', 'dep', 'clm'];

const DO: Record<string, { lab: string; kind: 'pri' | 'warn' | 'mute'; stK: string; tone: Tone; why: string }> = {
  ok: { lab: 'Phát DO', kind: 'pri', stK: 'Sẵn sàng', tone: 'ok', why: 'Đã thanh toán lô' },
  issued: { lab: 'Đã phát', kind: 'mute', stK: 'Đã phát', tone: 'ok', why: 'Phát 20/Oct 15:12' },
  unpaid: { lab: 'Đề nghị phát', kind: 'warn', stK: 'Khoá', tone: 'warn', why: 'Chưa thanh toán lô' },
  overdue: { lab: 'Đề nghị phát', kind: 'warn', stK: 'Khoá', tone: 'err', why: 'Quá hạn công nợ 38 ngày' },
  over: { lab: 'Đề nghị phát', kind: 'warn', stK: 'Khoá', tone: 'err', why: 'Vượt hạn mức' },
};
const LANE_TONE = { G: 'ok', Y: 'warn', R: 'err', X: 'vio' } as const;
const CLAIM_ST: Record<ClaimState, { k: string; tone: Tone }> = { open: { k: 'Mở', tone: 'pri' }, chase: { k: 'Đang đòi', tone: 'warn' }, closed: { k: 'Chốt', tone: 'ok' }, bod: { k: 'Chờ BOD duyệt', tone: 'vio' } };
const day = (d: string): number => parseInt(d, 10);

const tx = (k: string, sub?: string, o?: Omit<Extract<Cell, { t: 'tx' }>, 't' | 'k' | 's'>): Cell => ({ t: 'tx', k, s: sub, ...o });
const dash: Cell = { t: 'tx', k: '—', tone: 'lb' };
const txt = (cells: Cell[]): string => cells.map((c) => (c.t === 'tx' || c.t === 'chip' ? `${c.k} ${c.s ?? ''}` : c.t === 'clock' ? c.s : c.t === 'btn' ? c.k : '')).join(' ');

function shipRows(list: readonly ImportShipment[]): Row[] {
  return list.map((r) => {
    const d = DO[r.doState]!;
    const cells: Cell[] = [
      tx(r.id, r.cus, { fw: true, tone: 'link' }), tx(r.cargo), tx(r.vessel, r.lane),
      tx(r.eta.replace('/2026', ''), undefined, { tone: day(r.eta) === TODAY ? 'warn' : undefined }),
      tx(r.preAlert, undefined, { fw: true, tone: r.preAlert === 'Đủ' ? 'ok' : 'warn' }),
      { t: 'chip', k: d.stK, tone: d.tone, s: d.why },
      { t: 'btn', k: d.lab, tone: d.kind, modal: isDoLocked(r.doState) ? 'do' : undefined, tip: r.doState === 'ok' ? undefined : 'DO tự khoá khi khách chưa thanh toán lô hoặc quá hạn công nợ' },
      tx(r.refs.split(' · ')[0]!, undefined, { tone: 'tx2' }),
    ];
    return { id: r.id, text: `${txt(cells)} ${r.refs}`, cells };
  });
}
function freeRows(list: readonly ContainerFree[]): Row[] {
  return list.map((c) => {
    const left = freeLeft(c);
    const act: Cell = left < 0 ? { t: 'btn', k: 'Xác nhận số ngày', tone: 'warn', modal: 'days' } : left <= 2 ? { t: 'btn', k: 'Báo khách', tone: 'pri' } : { t: 'btn', k: 'Xem', tone: 'mute' };
    const cells: Cell[] = [
      tx(c.cont, c.phase === 'RF' ? '40RF · cắm điện' : '40HC', { fw: true }), tx(c.job, c.cus, { tone: 'link' }), tx(c.carrier, c.kind),
      tx(`${c.freeCarrier} / ${c.freeCustomer} ngày`, `Đã dùng ${c.used} ngày`), { t: 'clock', left, total: c.freeCustomer, s: c.start }, act,
    ];
    return { id: c.cont, text: txt(cells), cells };
  });
}
function laneRows(list: readonly CustomsDecl[]): Row[] {
  return list.map((d) => {
    const fee: Cell = d.offTariff === 'none' ? dash : d.offTariff === 'ok' ? { t: 'chip', k: 'Khách đã xác nhận', tone: 'ok', s: d.offTariffNote.split(' · ')[0] } : { t: 'chip', k: 'Chưa gửi khách', tone: 'err', s: d.offTariffNote.split(' · ')[0] };
    const act: Cell = d.lane === 'G' ? { t: 'btn', k: 'Xem', tone: 'mute' } : d.offTariff === 'wait' ? { t: 'btn', k: 'Gửi khách xác nhận phí', tone: 'warn', modal: 'fee' } : { t: 'btn', k: 'Gửi mail khách', tone: 'pri', tip: 'Mail soạn sẵn theo mẫu, CS phụ trách gửi' };
    const cells: Cell[] = [tx(d.id, `OPS ${d.owner}`, { fw: true }), tx(d.job, d.cus, { tone: 'link' }), { t: 'chip', k: LANE_LABEL[d.lane], tone: LANE_TONE[d.lane] }, tx(d.schedule), fee, act];
    return { id: d.id, text: txt(cells), cells };
  });
}
function reeferRows(list: readonly ReeferCont[]): Row[] {
  return list.map((r) => {
    const [temp, at] = r.received.split(' · ');
    const photo: Cell = r.status === 'dev' ? { t: 'chip', k: `${temp} · lệch 6,8 °C`, tone: 'err', s: at } : r.status === 'miss' ? { t: 'chip', k: 'Chưa có ảnh', tone: 'warn', s: 'HANA Mov chưa gửi' } : tx(temp!, at, { tone: 'ok', fw: true });
    const [dTemp, dAt] = r.dropped.split(' · ');
    const act: Cell = r.status === 'dev' ? { t: 'btn', k: 'Mở sự cố K4', tone: 'warn' } : r.status === 'miss' ? { t: 'btn', k: 'Nhắc điều độ chụp ảnh', tone: 'pri' } : { t: 'btn', k: 'Xem ảnh', tone: 'mute' };
    const cells: Cell[] = [tx(r.cont, '40RF', { fw: true }), tx(r.job, r.cus, { tone: 'link' }), tx(r.setTemp, `Vent ${r.vent}`), photo, tx(dTemp!, dAt), tx(r.pti, undefined, { fw: true, tone: r.pti === 'Đạt' ? 'ok' : 'warn' }), act];
    return { id: r.cont, text: txt(cells), cells };
  });
}
function depositRows(list: readonly Deposit[]): Row[] {
  return list.map((r) => {
    const ded = r.money.deducted > 0 ? tx(r.reason, r.evidence) : dash;
    const dec: Cell =
      r.st === 'decide' ? { t: 'chip', k: 'Chờ Trưởng CS', tone: 'warn', s: r.evidence }
      : r.st === 'hana' ? { t: 'chip', k: 'HANA chịu', tone: 'vio', s: r.evidence }
      : r.st === 'late' ? { t: 'chip', k: 'Quá hạn hoàn 9 ngày', tone: 'err', s: 'task CDR' }
      : r.st === 'done' ? { t: 'chip', k: 'Đã hoàn đủ', tone: 'ok' }
      : { t: 'chip', k: 'Chờ hoàn', tone: 'pri' };
    const act: Cell = r.st === 'decide' ? { t: 'btn', k: 'Quyết định khoản trừ', tone: 'warn', modal: 'ded' } : r.st === 'late' ? { t: 'btn', k: 'Đòi hãng hoàn cược', tone: 'pri' } : { t: 'btn', k: 'Xem', tone: 'mute' };
    const cells: Cell[] = [tx(r.cont, r.carrier, { fw: true }), tx(r.cus), tx(r.returned, `→ ${r.expectedRefund}`), ded, dec, act];
    return { id: r.cont, text: txt(cells), cells };
  });
}
function claimRows(list: readonly Claim[]): Row[] {
  return list.map((r) => {
    const st = CLAIM_ST[r.st];
    const urgent = r.due !== '—' && day(r.due) - TODAY <= 1;
    const cells: Cell[] = [
      tx(r.id, r.job, { fw: true, tone: 'link' }), tx(r.cus), tx(r.what), tx(r.party),
      tx(r.due, r.due === '—' ? undefined : urgent ? 'task CLM · gấp' : 'task CLM', { tone: urgent ? 'err' : undefined }),
      { t: 'chip', k: st.k, tone: st.tone, s: r.st === 'bod' ? 'vượt ngưỡng duyệt' : undefined },
    ];
    return { id: r.id, text: txt(cells), cells };
  });
}

function CellView({ c, onAct }: { c: Cell; onAct: (c: Extract<Cell, { t: 'btn' }>) => void }): ReactNode {
  if (c.t === 'tx')
    return (
      <>
        <div className={`${s.main} ${c.fw ? s.fw : ''} ${c.tone ? s[c.tone] : ''}`} title={c.k}>{c.k}</div>
        {c.s && <div className={s.sub}>{c.s}</div>}
      </>
    );
  if (c.t === 'chip')
    return (
      <>
        <span className={`${s.pill} ${s[`p_${c.tone}`]}`}><span className={s.dot} aria-hidden="true" />{c.k}</span>
        {c.s && <div className={s.sub}>{c.s}</div>}
      </>
    );
  if (c.t === 'clock') {
    const over = c.left < 0, soon = !over && c.left <= 2;
    const pct = Math.max(0, Math.min(100, Math.round(((c.total - Math.max(c.left, 0)) / c.total) * 100)));
    return (
      <>
        <div className={s.clock}>
          <div className={`${s.bar} ${over ? s.over : soon ? s.soon : ''}`}><i style={{ width: `${pct}%` }} /></div>
          <b className={over ? s.err : soon ? s.warn : ''}>{over ? `quá ${-c.left} ngày` : `còn ${c.left} ngày`}</b>
        </div>
        <div className={s.sub}>{c.s}</div>
      </>
    );
  }
  return (
    <button type="button" className={`${s.act} ${s[`a_${c.tone}`]}`} title={c.tip} onClick={(e) => { e.stopPropagation(); onAct(c); }}>{c.k}</button>
  );
}

function readState(): ScreenState | null {
  const v = new URLSearchParams(location.search).get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : null;
}

function ImportTable() {
  const t = useT();
  const toast = useToast();
  const ship = useList('importShipment', { limit: 5000 });
  const free = useList('importContainerFree', { limit: 5000 });
  const decl = useList('importDecl', { limit: 5000 });
  const rf = useList('importReefer', { limit: 5000 });
  const dep = useList('importDeposit', { limit: 5000 });
  const clm = useList('importClaim', { limit: 5000 });
  const forced = readState();
  const [tab, setTab] = useState<ImportTab>('imp');
  const [quick, setQuick] = useState('all');
  const [q, setQ] = useState('');
  const [dense, setDense] = useState<'std' | 'compact'>('std');
  const [dock, setDock] = useState(true);
  const [qv, setQv] = useState<{ tab: ImportTab; id: string } | null>(null);
  const [wide, setWide] = useState(() => matchMedia('(min-width: 1800px)').matches);
  useEffect(() => {
    const mq = matchMedia('(min-width: 1800px)');
    const on = () => setWide(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  const noWrite = () => toast({ msg: 'Chưa mở ghi ở bản này' });
  const [modal, setModal] = useState<ModalReq | null>(null);
  const [claim, setClaim] = useState<{ from?: boolean } | null>(null);
  const act = (b: Extract<Cell, { t: 'btn' }>, id: string) => (b.modal ? setModal({ kind: b.modal, id }) : b.k === 'Mở sự cố K4' ? setClaim({ from: true }) : noWrite());
  const detailAct = (k: string, id: string): boolean => {
    const m: Record<string, ModalKind> = { 'Đề nghị phát DO': 'do', 'Xác nhận số ngày': 'days', 'Phí ngoài biểu': 'fee', 'Quyết định khoản trừ': 'ded', 'Chốt hồ sơ': 'close' };
    if (m[k]) { setModal({ kind: m[k]!, id }); return true; }
    if (k.startsWith('Mở sự cố')) { setClaim({ from: k.endsWith('K4') }); return true; }
    return false;
  };
  const pickTab = (k: ImportTab) => { setTab(k); setQuick('all'); setQv(null); };

  const S = ship.data?.items ?? [], F = free.data?.items ?? [], D = decl.data?.items ?? [], R = rf.data?.items ?? [], P = dep.data?.items ?? [], C = clm.data?.items ?? [];
  const counts: Record<ImportTab, number> = { imp: S.length, dd: 3, lane: 3, rf: 2, dep: 2, clm: 3 }; // số cố định theo board (TABS)

  const { all, sums } = useMemo((): { all: Row[]; sums: Sum[] } => {
    switch (tab) {
      case 'imp': {
        const list = quickShipments(S, quick as 'all' | 'lock' | 'eta');
        return { all: shipRows(list), sums: [{ k: 'Lô', v: String(list.length) }, { k: 'DO khoá', v: String(S.filter((x) => isDoLocked(x.doState)).length), tone: 'err' }, { k: 'ETA hôm nay', v: '2' }] };
      }
      case 'dd': {
        const list = quickContainers(F, quick as 'all' | 'soon' | 'over');
        return { all: freeRows(list), sums: [{ k: 'Sắp hết', v: String(F.filter((c) => freeLeft(c) >= 0 && freeLeft(c) <= 2).length), tone: 'warn' }, { k: 'Quá hạn', v: String(F.filter((c) => freeLeft(c) < 0).length), tone: 'err' }, { k: 'Chờ xác nhận ngày', v: String(F.filter((c) => freeLeft(c) < 0).length) }] };
      }
      case 'lane': {
        const list = quickDecls(D, quick as 'all' | 'ry' | 'fee');
        return { all: laneRows(list), sums: [{ k: 'Vàng/đỏ/soi', v: String(D.filter((d) => d.lane !== 'G').length), tone: 'warn' }, { k: 'Chờ khách xác nhận phí', v: '1', tone: 'err' }] };
      }
      case 'rf': {
        const list = R.filter((r) => (quick === 'dev' ? r.status === 'dev' : quick === 'miss' ? r.status === 'miss' : true));
        return { all: reeferRows(list), sums: [{ k: 'Cont lạnh', v: String(R.length) }, { k: 'Lệch', v: '1', tone: 'err' }, { k: 'Thiếu ảnh', v: '1', tone: 'warn' }] };
      }
      case 'dep': {
        const list = P.filter((r) => (quick === 'late' ? r.st === 'late' : quick === 'ded' ? r.money.deducted > 0 : true));
        return { all: depositRows(list), sums: [{ k: 'Cont đang giữ cược', v: String(P.filter((r) => r.st !== 'done').length) }, { k: 'Quá hạn hoàn', v: '1', tone: 'err' }] };
      }
      case 'clm': {
        const list = C.filter((r) => (quick === 'open' ? r.st === 'open' || r.st === 'chase' : quick === 'bod' ? r.st === 'bod' : true));
        return { all: claimRows(list), sums: [{ k: 'Đang mở', v: '2', tone: 'warn' }, { k: 'Chờ BOD', v: '1' }] };
      }
    }
  }, [tab, quick, S, F, D, R, P, C]);
  const rows = q.trim() ? all.filter((r) => norm(r.text).includes(norm(q.trim()))) : all;

  const def = TABS[tab];
  const loading = [ship, free, decl, rf, dep, clm].some((x) => x.isLoading);
  const err = [ship, free, decl, rf, dep, clm].find((x) => x.isError);
  let state: ScreenState = 'ready';
  if (forced) state = forced;
  else if (loading) state = 'loading';
  else if (err) state = err.error instanceof DataError && err.error.code === 'noperm' ? 'noperm' : 'error';
  const ready = state === 'ready' || state === 'noperm';
  const showCols = def.cols;
  const showTotal = showCols.reduce((a, c) => a + c[1], 0);

  const detail = qv && state === 'ready' ? buildQv(qv.tab, qv.id, { imp: S, dd: F, lane: D, rf: R, dep: P, clm: C }) : null;
  if (qv && detail) return <Page kind="table"><><ImportModal req={modal} data={{ imp: S, dd: F, lane: D, dep: P, clm: C }} onClose={() => setModal(null)} /><ClaimDrawer open={!!claim} from={claim?.from} onClose={() => setClaim(null)} /><ImportDetail q={detail} tabLabel={TABS[qv.tab].label} onBack={() => setQv(null)} onAct={(k) => detailAct(k, qv.id)} /></></Page>;

  return (
    <Page kind="table">
      <ImportModal req={modal} data={{ imp: S, dd: F, lane: D, dep: P, clm: C }} onClose={() => setModal(null)} />
      <ClaimDrawer open={!!claim} from={claim?.from} onClose={() => setClaim(null)} />
      <PageHeader findPad
        title="Hàng nhập & container"
        findHint
        actions={
          <>
            <Segmented kind="toggle" label="Mật độ" value={dense} onChange={setDense} items={[{ key: 'std', label: 'Chuẩn' }, { key: 'compact', label: 'Gọn' }]} />
            <HeaderTool label="Phân tích" icon={IconChartBar} />
            <HeaderTool label="Việc" icon={IconChecklist} />
            <HeaderTool label="Thông báo" icon={IconBell} />
            <Button variant="primary" icon={<IconPlus size={16} aria-hidden="true" />} onClick={() => setClaim({})}>{t('imp.openIssue')}</Button>
          </>
        }
      />
      <div className={`${s.body} ${wide && dock ? s.bodyDock : ''}`}>
      <div className={`${s.page} ${dense === 'compact' ? s.dense : ''}`}>
        <div className={s.seg} role="tablist" aria-label="Mục" style={{ alignSelf: 'flex-start' }}>
          {ORDER.map((k) => (
            <button key={k} type="button" role="tab" className={s.tab} aria-selected={tab === k} onClick={() => pickTab(k)}>
              {TABS[k].label}
              {ready && <span className={s.tabN}>{counts[k]}</span>}
            </button>
          ))}
        </div>
        <div className={s.tb}>
          <label className={s.search}>
            <IconSearch size={16} aria-hidden="true" />
            <input aria-label="Tìm" placeholder="Job / container / tờ khai / khách" value={q} onChange={(e) => setQ(e.target.value)} />
          </label>
          <div className={s.seg} role="group" aria-label="Lọc nhanh">
            {def.quick.map(([k, label]) => (
              <button key={k} type="button" className={s.q} aria-pressed={quick === k} onClick={() => setQuick(k)}>{label}</button>
            ))}
          </div>
          <button type="button" className={s.filter} onClick={noWrite}>
            <IconPlus size={16} aria-hidden="true" />
            {t('imp.filter')}
          </button>
          {ready && (
            <div className={s.sums}>
              {sums.map((m) => (
                <span key={m.k}>{m.k} <b className={m.tone ? s[m.tone] : ''}>{m.v}</b></span>
              ))}
            </div>
          )}
        </div>
        {state === 'noperm' && (
          <div role="note" className={s.note}>
            <IconLock size={16} aria-hidden="true" />
            {t('imp.ownOnly')}
          </div>
        )}
        <StateView
          state={ready && rows.length === 0 ? 'empty' : ready ? 'ready' : state}
          title={def.emptyK}
          desc={def.emptyS}
          icon={<IconPackageImport size={26} stroke={1.8} />}
          action={state === 'error' ? <button type="button" className={s.act} onClick={() => { for (const x of [ship, free, decl, rf, dep, clm]) void x.refetch(); }}>{t('imp.retry')}</button> : undefined}
          skeleton={<Skeleton rows={9} />}
        >
          <section className={s.card} aria-label={def.label}>
            <table className={s.table} aria-label={def.label} aria-rowcount={rows.length + 1}>
              <colgroup>
                {showCols.map((c, i) => (
                  <col key={i} style={{ width: `${((c[1] / showTotal) * 100).toFixed(3)}%` }} />
                ))}
              </colgroup>
              <thead>
                <tr>
                  {showCols.map((c, i) => (
                    <th key={i} scope="col" className={c[0] === '' ? s.r : undefined}>{c[0]}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} onClick={() => setQv({ tab, id: r.id })}>
                    {r.cells.map((c, i) => (
                      <td key={i} className={c.t === 'btn' ? s.r : undefined}>
                        <CellView c={c} onAct={(b) => act(b, r.id)} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            <div className={s.foot}>
              <span>{rows.length} dòng · {def.foot}</span>
              <span title="Dữ liệu cập nhật 22/Oct/2026 09:40" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <IconRefresh size={13} aria-hidden="true" />
                22/Oct 09:40
              </span>
            </div>
          </section>
        </StateView>
      </div>
      {wide && dock && <ImportDock containers={F} claims={C} onClose={() => setDock(false)} onOpenClaims={() => pickTab('clm')} />}
      </div>
    </Page>
  );
}

export function ImportList() {
  const layout = useLayout();
  return layout === 'pad' || layout === 'phone' ? <ImportCompact phone={layout === 'phone'} /> : <ImportTable />;
}
